/**
 * ProfilePage Component
 *
 * User profile settings and account management.
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Cropper, { Area } from 'react-easy-crop';
import toast from 'react-hot-toast';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as firebaseUpdatePassword,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { DashboardLayout } from './components';
import { useAuth } from '../../contexts';
import { PhoneInput } from '../../components/common/PhoneInput';
import { uploadFile, getUserPhotoPath } from '../../services/storage';
import { auth } from '../../services/firebase';

const ProfileContent: React.FC = () => {
  const { user, updateProfile, logout } = useAuth();
  const [activeSection, setActiveSection] = useState<'profile' | 'security' | 'account'>('profile');

  return (
    <>
      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { key: 'profile', label: 'Personal Info' },
          { key: 'security', label: 'Login & Security' },
          { key: 'account', label: 'Account' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSection(tab.key as typeof activeSection)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeSection === tab.key
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {activeSection === 'profile' && <ProfileSection user={user} onUpdate={updateProfile} />}
        {activeSection === 'security' && <SecuritySection />}
        {activeSection === 'account' && <AccountSection user={user} onLogout={logout} />}
      </div>
    </>
  );
};

export const ProfilePage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  if (embedded) return <ProfileContent />;

  return (
    <DashboardLayout title="Profile Settings">
      <ProfileContent />
    </DashboardLayout>
  );
};

// Helper: crop the image using canvas based on the cropped area from react-easy-crop
const getCroppedImg = (imageSrc: string, pixelCrop: Area, size = 400): Promise<File> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        size,
        size
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Could not create blob'));
            return;
          }
          resolve(new File([blob], 'profile.jpg', { type: 'image/jpeg', lastModified: Date.now() }));
        },
        'image/jpeg',
        0.85
      );
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
};

// Crop Modal
interface CropModalProps {
  imageSrc: string;
  onConfirm: (croppedFile: File) => void;
  onClose: () => void;
}

const CropModal: React.FC<CropModalProps> = ({ imageSrc, onConfirm, onClose }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setIsSaving(true);
    try {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels);
      onConfirm(croppedFile);
    } catch {
      toast.error('Failed to crop image. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900">Crop Profile Photo</h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="relative w-full h-80 bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-6 py-3">
          <label className="flex items-center gap-3 text-sm text-gray-600">
            <ZoomOutIcon className="w-4 h-4" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-primary-500"
            />
            <ZoomInIcon className="w-4 h-4" />
          </label>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleConfirm}
            disabled={isSaving}
            className="flex-1 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-50"
          >
            {isSaving ? 'Processing...' : 'Save Photo'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Profile Section
interface ProfileSectionProps {
  user: { uid?: string; displayName?: string; email?: string; phone?: string; photoURL?: string } | null;
  onUpdate: (data: Partial<{ displayName: string; phone: string; photoURL: string }>) => Promise<void>;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({ user, onUpdate }) => {
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [photoURL, setPhotoURL] = useState(user?.photoURL || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Update state when user data loads
  React.useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setPhone(user.phone || '');
      setPhotoURL(user.photoURL || '');
    }
  }, [user]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    // Read file and open crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Reset input so re-selecting the same file works
    e.target.value = '';
  };

  const handleCropConfirm = async (croppedFile: File) => {
    setCropImage(null);
    if (!user?.uid) return;

    setIsUploadingPhoto(true);
    try {
      const storagePath = getUserPhotoPath(user.uid, 'profile.jpg');
      const { url } = await uploadFile(storagePath, croppedFile);
      setPhotoURL(url);
      await onUpdate({ photoURL: url });
      toast.success('Profile photo updated!');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await onUpdate({ displayName, phone });
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>

        {/* Profile Photo Upload */}
        <div className="flex items-center gap-6">
          <div className="relative">
            {photoURL ? (
              <img
                src={photoURL}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                <UserIcon className="w-12 h-12 text-gray-400" />
              </div>
            )}
            {isUploadingPhoto && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingPhoto}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isUploadingPhoto ? 'Uploading...' : 'Change Photo'}
            </button>
            <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF. Max 10MB.</p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Contact support to change your email
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <PhoneInput
              value={phone}
              onChange={setPhone}
              defaultCountryCode="+961"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </form>

      {/* Crop Modal */}
      {cropImage && (
        <CropModal
          imageSrc={cropImage}
          onConfirm={handleCropConfirm}
          onClose={() => setCropImage(null)}
        />
      )}
    </>
  );
};

// Security Section
const inputClass =
  'w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

/**
 * Reauthenticate the signed-in user with their current password.
 * Required by Firebase before sensitive operations (password/email change).
 */
async function reauthenticate(currentPassword: string) {
  const current = auth.currentUser;
  if (!current || !current.email) throw new Error('Not authenticated');
  const credential = EmailAuthProvider.credential(current.email, currentPassword);
  await reauthenticateWithCredential(current, credential);
}

function securityErrorMessage(err: unknown): string {
  const code = (err as { code?: string }).code || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Current password is incorrect.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/email-already-in-use':
      return 'An account already exists with that email.';
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/requires-recent-login':
      return 'For security, log out and back in, then try again.';
    case 'auth/operation-not-allowed':
      return 'This change is not allowed right now. Contact support.';
    default:
      return 'Something went wrong. Try again.';
  }
}

const SecuritySection: React.FC = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [emailPassword, setEmailPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [verificationSentTo, setVerificationSentTo] = useState<string | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;

    setIsLoading(true);
    try {
      await reauthenticate(currentPassword);
      await firebaseUpdatePassword(auth.currentUser!, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password updated.');
    } catch (error) {
      toast.error(securityErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const target = newEmail.trim().toLowerCase();
    if (!target || target === user?.email?.toLowerCase()) {
      toast.error('Enter a new email address.');
      return;
    }

    setEmailLoading(true);
    try {
      await reauthenticate(emailPassword);
      await verifyBeforeUpdateEmail(auth.currentUser!, target);
      setVerificationSentTo(target);
      setEmailPassword('');
      setNewEmail('');
      toast.success('Verification link sent.');
    } catch (error) {
      toast.error(securityErrorMessage(error));
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="space-y-12">
      <form onSubmit={handleChangePassword} className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>

        <div className="max-w-md space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              className={inputClass}
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading || newPassword !== confirmPassword}
          className="px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Updating...' : 'Update Password'}
        </button>
      </form>

      <form onSubmit={handleChangeEmail} className="space-y-6 border-t border-gray-200 pt-10">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Change Email</h3>
          <p className="text-sm text-gray-500 mt-1">
            Signed in as <strong>{user?.email}</strong>. We'll send a verification
            link to your new address. Your email changes only after you click it.
          </p>
        </div>

        {verificationSentTo && (
          <div className="max-w-md p-4 bg-primary-50 border border-primary-200 rounded-lg text-sm text-gray-700">
            Verification link sent to <strong>{verificationSentTo}</strong>. Check
            that inbox to confirm the change, then log in with your new email.
          </div>
        )}

        <div className="max-w-md space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Email
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              autoComplete="email"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              required
              autoComplete="current-password"
              className={inputClass}
            />
            <p className="text-xs text-gray-500 mt-1">
              We ask for your password again to protect your account.
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={emailLoading}
          className="px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
        >
          {emailLoading ? 'Sending...' : 'Send Verification Link'}
        </button>
      </form>
    </div>
  );
};

// Account Section
interface AccountSectionProps {
  user: { role?: string } | null;
  onLogout: () => Promise<void>;
}

const AccountSection: React.FC<AccountSectionProps> = ({ user, onLogout }) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Type</h3>
        <p className="text-gray-600 mb-4">
          You're currently registered as a <strong className="capitalize">{user?.role || 'Guest'}</strong>.
        </p>
        {user?.role === 'guest' && (
          <Link
            to="/become-host"
            className="inline-flex items-center gap-2 px-4 py-2 border border-primary-500 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
          >
            <HomeIcon className="w-5 h-5" />
            Become a Host
          </Link>
        )}
      </div>

      <div className="border-t border-gray-200 pt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Actions</h3>
        <div className="space-y-4">
          <button
            onClick={() => {
              if (confirm('Are you sure you want to log out?')) {
                onLogout();
              }
            }}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <LogoutIcon className="w-5 h-5" />
            Log out
          </button>

          <button className="flex items-center gap-2 text-gray-500 hover:text-gray-700">
            <DownloadIcon className="w-5 h-5" />
            Download my data (GDPR)
          </button>

          <button className="flex items-center gap-2 text-red-600 hover:text-red-700">
            <TrashIcon className="w-5 h-5" />
            Delete account
          </button>
        </div>
      </div>
    </div>
  );
};

// Icons
const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const HomeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const LogoutIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

const DownloadIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ZoomInIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
  </svg>
);

const ZoomOutIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
  </svg>
);

export default ProfilePage;
